
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { getAdminOverview, operationsLogout } from './adminApi';

import './AdminDashboard.css';

import RestaurantProfile from './RestaurantProfile';
import AnalyticsOverview from './AnalyticsOverview';
import Categories from './Categories';
import MenuItems from './MenuItems';
import Staff from './Staff';
import TablesQR from './TablesQR';
import Reports from './Reports';
import Settings from './Settings';
import ChangePassword from './ChangePassword';
import OwnerSecurity from './OwnerSecurity';

const modules = [
  'Overview',
  'Restaurant Profile',
  'Categories',
  'Menu Items',
  'Staff',
  'Tables & QR',
  'Reports',
  'Settings',
  'Change Password',
  'Security Management',
];

export default function AdminDashboard() {
  const { restaurantId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [active, setActive] = useState('Overview');
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    let mounted = true;

    setLoading(true);
    setError('');

    getAdminOverview(restaurantId)
      .then((result) => {
        if (mounted) {
          setData(result);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (mounted) {
          setError(err.message);
          setLoading(false);

          if (err.status === 401 || err.status === 403) {
            navigate(`/admin/${restaurantId}`, {
              replace: true,
            });
          }
        }
      });

    return () => {
      mounted = false;
    };
  }, [restaurantId, navigate]);

  async function logout() {
    try {
      await operationsLogout(restaurantId);
    } finally {
      navigate(`/admin/${restaurantId}`, {
        replace: true,
      });
    }
  }

  if (loading) {
    return (
      <div className="ra-center">
        Loading restaurant admin…
      </div>
    );
  }

  if (error) {
    return (
      <div className="ra-center ra-error">
        {error}
      </div>
    );
  }

  return (
    <div className="ra-app">
      <aside className="ra-sidebar">
        <div className="ra-mobile-brandbar">
          <span>Restaurant Admin</span>
          <button type="button" className="ra-menu-toggle" aria-label={menuOpen ? 'Close menu' : 'Open menu'} aria-expanded={menuOpen} aria-controls="ra-dashboard-nav" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>
        <div className="ra-brand">
          Restaurant Admin
          <span>{data.restaurant.name}</span>
        </div>

        <nav id="ra-dashboard-nav" className={menuOpen ? "ra-nav-open" : ""} aria-label="Admin modules">
          {modules.map((name) => (
            <button
              key={name}
              type="button"
              className={
                active === name
                  ? 'ra-nav active'
                  : 'ra-nav'
              }
              onClick={() => { setActive(name); setMenuOpen(false); }}
            >
              {name}
            </button>
          ))}
        </nav>

      </aside>

      <div className="ra-main">
        <header className="ra-header">
          <div>
            <small>Restaurant administration</small>
            <h1>{active}</h1>
          </div>

          <div className="ra-user">
            <a className="ra-operations-top" href={`/operations/${restaurantId}/dashboard`}>
              Open Operations Dashboard ↗
            </a>
            <span>
              {data.user.username} · {data.role}
            </span>

            <button
              type="button"
              onClick={logout}
            >
              Log out
            </button>
          </div>
        </header>

        {active === 'Overview' ? (
          <AnalyticsOverview restaurantId={restaurantId} />

        ) : active === 'Restaurant Profile' ? (
          <RestaurantProfile
            restaurantId={restaurantId}
            onSaved={(profile) =>
              setData((previous) => ({
                ...previous,
                restaurant: {
                  ...previous.restaurant,
                  name: profile.name,
                },
              }))
            }
          />

        ) : active === 'Categories' ? (
          <Categories restaurantId={restaurantId} />

        ) : active === 'Menu Items' ? (
          <MenuItems restaurantId={restaurantId} />

        ) : active === 'Staff' ? (
          <Staff restaurantId={restaurantId} />

        ) : active === 'Tables & QR' ? (
          <TablesQR restaurantId={restaurantId} />

        ) : active === 'Reports' ? (
          <Reports restaurantId={restaurantId} />

        ) : active === 'Settings' ? (
          <Settings restaurantId={restaurantId} />

        ) : active === 'Security Management' ? (
          <OwnerSecurity restaurantId={restaurantId} role={data.role} />

        ) : active === 'Change Password' ? (
          <ChangePassword restaurantId={restaurantId} />

        ) : null}
      </div>
    </div>
  );
}